import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * What the 2D-world canvas draws: the CanvasItem world and the native Control canvas in one
 * orthographic view, with a camera that tracks the stage's pan and zoom. `World2DCanvas` mounts
 * it inside its `<Canvas>`.
 */
import { lazy, useLayoutEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { CanvasWorkspaceProvider } from '../../contexts/CanvasWorkspaceContext.js';
import { EditedRootProvider } from '../../contexts/InstanceOwnersContext.js';
import { SceneResourcesProvider } from '../../SceneResourcesContext.js';
import { NodeDispatcher } from '../../NodeDispatcher.js';
import { CanvasWorldScope } from '../../environment/WorldEnvironmentScope.js';
import { world2DCameraPose } from './world2DCamera.js';
import { CanvasLighting2DProvider } from '../../lighting2d/CanvasLighting2D.js';
import { useCanvasModulateColor } from '../../canvasModulate.js';
import { ViewportPassOrchestrator } from '../../contexts/ViewportPassRegistryContext.js';
import { ControlRasterLayer } from '../../../nodes/viewport/subviewport/ControlRasterLayer.js';
import { TiledUploadDriver } from '../../tiledUpload/TiledUploadDriver.js';
import { ProjectClearColor } from '../../ProjectClearColor.js';
import { CanvasFrameRender } from './CanvasFrameRender.js';
import { PendingSuspense } from '../../../resources/PendingSuspense.js';
import { ScreenshotBridge } from '../../ScreenshotBridge.js';
// Through the controls barrel, whose side-effect imports register every Control
// type: the component file alone registers none. Lazy, so the registrations stay
// out of the initial bundle until a stage renders Controls.
const ControlCanvasLayer = lazy(() => import('../../controls/index.js').then((m) => ({ default: m.ControlCanvasLayer })));
/** Keeps the ortho camera glued to the stage's pan/zoom transform. */
function CameraRig({ pan, zoom }) {
    const camera = useThree((s) => s.camera);
    const size = useThree((s) => s.size);
    const { x: panX, y: panY } = pan;
    useLayoutEffect(() => {
        const pose = world2DCameraPose({ x: panX, y: panY }, zoom, size.width, size.height);
        camera.position.set(pose.x, pose.y, 1000);
        camera.zoom = pose.zoom;
        camera.updateProjectionMatrix();
    }, [camera, panX, panY, zoom, size.width, size.height]);
    return null;
}
export function World2DContents({ nodes, internalResources, externalResources, editableInstances, pan, zoom, }) {
    const canvasModulate = useCanvasModulateColor(nodes);
    return (_jsx(TiledUploadDriver, { children: _jsxs(CanvasWorkspaceProvider, { workspace: "2d", children: [_jsx(SceneResourcesProvider, { internalResources: internalResources, externalResources: externalResources, children: _jsxs(EditedRootProvider, { editableInstances: editableInstances, children: [_jsx(CameraRig, { pan: pan, zoom: zoom }), _jsx(ProjectClearColor, {}), _jsx(CanvasFrameRender, {}), _jsxs(CanvasLighting2DProvider, { canvasModulate: canvasModulate, children: [_jsx(CanvasWorldScope, { children: _jsx(NodeDispatcher, { nodes: nodes }) }), _jsx(PendingSuspense, { children: _jsx(ControlCanvasLayer, { nodes: nodes }) }), _jsx(ControlRasterLayer, { nodes: nodes, internalResources: internalResources, externalResources: externalResources })] })] }) }), _jsx(ViewportPassOrchestrator, {}), _jsx(ScreenshotBridge, {})] }) }));
}
//# sourceMappingURL=World2DContents.js.map